/**
 * UpgradePrompt - Shows upgrade banner when approaching limits
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, TrendingUp, X } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import type { UsageSummary } from '@neokanban/types';

interface UpgradePromptProps {
  usage: UsageSummary;
  planName?: string;
  className?: string;
  dismissible?: boolean;
}

interface LimitWarning {
  resource: string;
  current: number;
  limit: number;
  percentage: number;
}

export const UpgradePrompt: React.FC<UpgradePromptProps> = ({
  usage,
  planName = 'your plan',
  className = '',
  dismissible = true,
}) => {
  const navigate = useNavigate();
  const [isDismissed, setIsDismissed] = useState(false);

  // Check which resources are near or at limits (80% threshold)
  const warnings: LimitWarning[] = [];

  const checkLimit = (resource: string, current: number, limit: number) => {
    if (limit === -1) return; // Unlimited
    if (limit <= 0) return; // Feature disabled (0) or invalid limit — avoid divide-by-zero
    const percentage = (current / limit) * 100;
    if (percentage >= 80) {
      warnings.push({ resource, current, limit, percentage });
    }
  };

  checkLimit('Team Members', usage.members.current, usage.members.limit);
  checkLimit('Projects', usage.projects.current, usage.projects.limit);
  checkLimit('Storage', usage.storageMb.current, usage.storageMb.limit);
  checkLimit('Transcription Minutes', usage.transcriptionMinutes.current, usage.transcriptionMinutes.limit);
  checkLimit('AI Extraction Credits', usage.extractionCredits.current, usage.extractionCredits.limit);

  // Don't show if no warnings or dismissed
  if (warnings.length === 0 || isDismissed) {
    return null;
  }

  // Get the most critical warning (highest percentage)
  const criticalWarning = warnings.reduce((prev, current) =>
    current.percentage > prev.percentage ? current : prev
  );

  const isAtLimit = criticalWarning.percentage >= 100;

  return (
    <Alert
      variant={isAtLimit ? 'destructive' : 'default'}
      className={`relative ${className}`}
    >
      <div className="flex items-start gap-3">
        {isAtLimit ? (
          <AlertTriangle className="h-5 w-5 mt-0.5" />
        ) : (
          <TrendingUp className="h-5 w-5 mt-0.5 text-yellow-600" />
        )}

        <div className="flex-1">
          <AlertTitle className="mb-1">
            {isAtLimit
              ? `You've reached your ${criticalWarning.resource} limit`
              : `You're approaching your ${criticalWarning.resource} limit`}
          </AlertTitle>
          <AlertDescription>
            {isAtLimit ? (
              <span>
                You've used{' '}
                <strong>
                  {criticalWarning.current.toLocaleString()} of{' '}
                  {criticalWarning.limit.toLocaleString()}
                </strong>{' '}
                {criticalWarning.resource.toLowerCase()} on {planName}. Upgrade to increase
                your limits.
              </span>
            ) : (
              <span>
                You've used{' '}
                <strong>
                  {criticalWarning.current.toLocaleString()} of{' '}
                  {criticalWarning.limit.toLocaleString()}
                </strong>{' '}
                {criticalWarning.resource.toLowerCase()} (
                {Math.round(criticalWarning.percentage)}% of your limit).
                {warnings.length > 1 && ` ${warnings.length - 1} other limit${warnings.length - 1 > 1 ? 's' : ''} also near capacity.`}
              </span>
            )}
          </AlertDescription>

          <div className="flex items-center gap-2 mt-3">
            <Button
              size="sm"
              onClick={() => navigate('/settings/subscription')}
              variant={isAtLimit ? 'destructive' : 'default'}
            >
              Upgrade Plan
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => navigate('/settings/subscription#usage')}
            >
              View Usage
            </Button>
          </div>
        </div>

        {dismissible && (
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2 h-6 w-6 p-0"
            onClick={() => setIsDismissed(true)}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Dismiss</span>
          </Button>
        )}
      </div>
    </Alert>
  );
};
